import { MarkerType, Position } from 'react-flow-renderer';
import { SubcontractorVerificationStatuses } from '../constants';

const PRIMARY_EDGE = {
    type: 'step',
    style: {
        stroke: '#5B9BD5',
        strokeWidth: 2,
    },
    markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#5B9BD5',
        width: 18,
        height: 18,
    },
};

const SUBCONTRACTOR_EDGE = {
    type: 'step',
    style: {
        stroke: '#C5CAD6',
        strokeWidth: 1.5,
        strokeDasharray: '6 4',
    },
    markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#C5CAD6',
        width: 16,
        height: 16,
    },
    label: 'Sub-contractor',
    labelStyle: {
        fill: '#6B7280',
        fontSize: 10,
        fontWeight: 600,
    },
    labelBgStyle: {
        fill: '#F9FAFB',
        fillOpacity: 0.95,
    },
    labelBgPadding: [4, 6],
    labelBgBorderRadius: 4,
};

/** Mock third parties selectable as Parent (until API is wired). */
export const MOCK_PARENT_THIRD_PARTY_OPTIONS = [
    { value: 'mock-dachser', label: 'DACHSER SE', secondaryId: 'TP-1001' },
    { value: 'mock-schenker', label: 'DB Schenker AG', secondaryId: 'TP-1002' },
    { value: 'mock-kuehne', label: 'Kuehne + Nagel International AG', secondaryId: 'TP-1003' },
    { value: 'mock-hellmann', label: 'Hellmann Worldwide Logistics SE & Co. KG', secondaryId: 'TP-1004' },
];

const GRAPH_TREE = {
    id: 'kraftverkehr',
    secondaryId: 'TP-2401',
    name: 'Kraftverkehr Müller',
    status: 'verified',
    shipments: 24,
    children: [
        {
            id: 'eastway',
            secondaryId: 'TP-2402',
            name: 'EastWay Transport',
            status: 'verified',
            shipments: 12,
            children: [],
        },
        {
            id: 'bavaria',
            secondaryId: 'TP-2403',
            name: 'Bavaria Logistik',
            status: 'verified',
            shipments: 9,
            children: [
                {
                    id: 'speedhaul',
                    secondaryId: 'TP-2404',
                    name: 'SpeedHaul',
                    status: 'verified',
                    shipments: 5,
                    children: [],
                },
            ],
        },
    ],
};

const GRAPH_TREE_SIBLINGS = [
    {
        id: 'translog',
        secondaryId: 'TP-2405',
        name: 'TransLog GmbH',
        status: 'verifying',
        shipments: 18,
        children: [
            {
                id: 'danube',
                secondaryId: 'TP-2406',
                name: 'Danube Freight',
                status: 'unknown',
                shipments: 3,
                children: [],
            },
        ],
    },
    {
        id: 'sudcargo',
        secondaryId: 'TP-2407',
        name: 'SüdCargo',
        status: 'verified',
        shipments: 31,
        children: [
            {
                id: 'alpen',
                secondaryId: 'TP-2408',
                name: 'Alpen Trans',
                status: 'verifying',
                shipments: 7,
                children: [],
            },
            {
                id: 'baltic',
                secondaryId: 'TP-2409',
                name: 'Baltic Carriers',
                status: 'verified',
                shipments: 11,
                children: [],
            },
        ],
    },
];

const NODE_POSITIONS = {
    root: { x: 380, y: 0 },
    kraftverkehr: { x: 80, y: 130 },
    translog: { x: 380, y: 130 },
    sudcargo: { x: 680, y: 130 },
    eastway: { x: 0, y: 280 },
    bavaria: { x: 160, y: 280 },
    speedhaul: { x: 160, y: 430 },
    danube: { x: 380, y: 280 },
    alpen: { x: 580, y: 280 },
    baltic: { x: 760, y: 280 },
};

const flattenTree = (node, parentName, parentSecondaryId, relationType, rows) => {
    rows.push({
        id: node.secondaryId,
        carrierId: node.id,
        name: node.name,
        parentTp: parentName,
        parentSecondaryId,
        relationType,
        status: node.status,
        shipments: node.shipments,
    });

    (node.children || []).forEach((child) => {
        flattenTree(child, node.name, node.secondaryId, 'Sub-contractor', rows);
    });
};

export const buildMockSubcontractorsTableRows = (rootParentLabel = 'DACHSER SE') => {
    const rows = [];

    flattenTree(GRAPH_TREE, rootParentLabel, 'TP-1001', 'Sub-contractor', rows);
    GRAPH_TREE_SIBLINGS.forEach((sibling) => {
        flattenTree(sibling, rootParentLabel, 'TP-1001', 'Sub-contractor', rows);
    });

    return rows;
};

export const buildMockSubcontractorsGraph = ({
    rootName = 'DACHSER',
    rootSubtitle = 'Logistic Company',
}) => {
    const nodes = [
        {
            id: 'root',
            type: 'subcontractorNode',
            position: NODE_POSITIONS.root,
            sourcePosition: Position.Bottom,
            data: {
                nodeKind: 'root',
                name: rootName,
                subtitle: rootSubtitle,
            },
        },
    ];

    const edges = [];

    const addBranch = (node, parentId, edgeStyle) => {
        const hasChildren = (node.children || []).length > 0;

        nodes.push({
            id: node.id,
            type: 'subcontractorNode',
            position: NODE_POSITIONS[node.id] || { x: 0, y: 0 },
            sourcePosition: Position.Bottom,
            targetPosition: Position.Top,
            data: {
                nodeKind: 'carrier',
                name: node.name,
                status: node.status,
                shipments: node.shipments,
                isLeaf: !hasChildren,
            },
        });

        edges.push({
            id: `e-${parentId}-${node.id}`,
            source: parentId,
            target: node.id,
            ...edgeStyle,
        });

        (node.children || []).forEach((child) => {
            addBranch(child, node.id, SUBCONTRACTOR_EDGE);
        });
    };

    addBranch(GRAPH_TREE, 'root', PRIMARY_EDGE);

    GRAPH_TREE_SIBLINGS.forEach((sibling) => {
        addBranch(sibling, 'root', PRIMARY_EDGE);
    });

    return { nodes, edges };
};

export const getSubcontractorStatusMeta = (statusKey) => (
    SubcontractorVerificationStatuses[statusKey]
    || SubcontractorVerificationStatuses.unknown
);

export const resolveParentThirdPartyOption = (parentId) => (
    MOCK_PARENT_THIRD_PARTY_OPTIONS.find(
        (option) => String(option.value) === String(parentId)
    ) || null
);
